import type { Interrupt, ThreadTask } from '@langchain/langgraph-sdk';
import {
  ArrowLeftIcon,
  Link01Icon,
  PlayCircleIcon,
} from '@langchain/untitled-ui-icons';

import { useCallback, useContext, useState } from 'react';

import { CodeData } from '@/components/Code/Code';
import { CopyInlineLink } from '@/components/CopyInlineLink';
import { Badge, Button, IconButton, Text } from '@/design-system';
import ArrowRightIcon from '@/icons/ArrowRightIcon.svg?react';

import { StateContext } from '../../../control/state';
import {
  foreignDataToTree,
  parseForeignData,
} from '../../../data/foreign-data';
import { getCheckpointId } from '../../../data/misc';
import { useEnterSubmit } from '../../../utils';
import { ForeignDataTreeView } from '../common/foreign-data-tree';
import { ContinueRefContext } from './GraphInputEditor.utils';

const InterruptFormContent = ({
  interrupt,
  task,
}: {
  interrupt: Interrupt;
  task: ThreadTask;
}) => {
  const [value, setValue] = useState('');

  const state = useContext(StateContext);
  const continueRef = useContext(ContinueRefContext);

  const checkpoint =
    state.threadHead?.checkpoint ?? getCheckpointId(state.threadHead);

  const interruptId =
    interrupt.id ??
    // TODO: Initially we returned `interrupt_id`, remove later
    (interrupt as { interrupt_id?: string; id: string }).interrupt_id;

  const onSubmit = useCallback(() => {
    const resume = interruptId ? { [interruptId]: value } : value;
    continueRef.current?.({ checkpoint, command: { resume } });
  }, [continueRef, checkpoint, interruptId, value]);

  useEnterSubmit(state.editingCheckpointId != null, onSubmit);

  return (
    <>
      <div className="rounded-md border border-brand-subtle bg-brand-tertiary p-3 text-sm">
        <span className="font-medium uppercase text-brand-primary">
          Interrupt
        </span>

        <div className="mt-1 text-primary">
          <ForeignDataTreeView
            data={foreignDataToTree({
              data: parseForeignData(interrupt.value),
            })}
          />
        </div>
      </div>

      <form>
        <div className="rounded-md border border-secondary bg-secondary">
          <div className="flex items-center justify-between">
            <span className="text-md flex px-3 py-2 font-medium tracking-tighter">
              Provide a value to resume execution for {task.name}
            </span>

            {interruptId && (
              <div className="flex items-center gap-2 px-2">
                <Text variant="xs" weight="medium" className="text-secondary">
                  Interrupt ID:
                </Text>
                <CopyInlineLink value={interruptId} hideIcon>
                  <Badge color="secondary">
                    <Link01Icon className="h-3 w-3" />
                  </Badge>
                </CopyInlineLink>
              </div>
            )}
          </div>
          <div className="h-px w-full border-t border-secondary" />
          <CodeData
            value={value}
            onChange={setValue}
            language="json"
            actions={
              <div className="m-3 flex items-center gap-2">
                <Button
                  onClick={onSubmit}
                  size="sm"
                  leftDecorator={PlayCircleIcon}
                >
                  Resume
                </Button>
              </div>
            }
          />
        </div>
      </form>
    </>
  );
};

export const InterruptFormAndContinue = ({
  interrupts,
}: {
  interrupts: { interrupt: Interrupt; task: ThreadTask }[];
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const selectedItem = interrupts.at(selectedIndex);

  if (!selectedItem) return null;

  return (
    <div className="flex flex-col gap-2">
      <InterruptFormContent
        interrupt={selectedItem.interrupt}
        task={selectedItem.task}
      />

      {interrupts.length > 1 && (
        <div className="flex items-center gap-1">
          <IconButton
            label="Previous interrupt"
            icon={ArrowLeftIcon}
            onClick={() => setSelectedIndex((prev) => prev - 1)}
            disabled={selectedIndex === 0}
            color="secondary"
            variant="plain"
            tooltipProps={{
              side: 'bottom',
            }}
          />
          <IconButton
            label="Next interrupt"
            icon={ArrowRightIcon}
            onClick={() => setSelectedIndex((prev) => prev + 1)}
            disabled={selectedIndex === interrupts.length - 1}
            color="secondary"
            variant="plain"
            tooltipProps={{
              side: 'bottom',
            }}
          />

          <Text variant="xs" weight="medium" className="text-secondary">
            {selectedIndex + 1} / {interrupts.length}
          </Text>
        </div>
      )}
    </div>
  );
};
